import type { RecurringFrequency, RecurringRule } from '@/domain'

import type { Database } from './database.types'
import type { CentimeClient } from './supabase'

type RuleRow = Database['public']['Tables']['recurring_rules']['Row']
type RuleInsert = Database['public']['Tables']['recurring_rules']['Insert']

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Lit la fréquence stockée en JSON ; la base garantit déjà sa forme (contrainte CHECK). */
export function frequencyFromJson(value: unknown): RecurringFrequency {
  if (!isRecord(value)) throw new Error('Fréquence invalide')
  const { kind } = value
  if (kind === 'weekly')
    return { kind, weekday: Number(value.weekday) as 1 | 2 | 3 | 4 | 5 | 6 | 7 }
  if (kind === 'yearly') return { kind, month: Number(value.month), day: Number(value.day) }
  if (kind === 'monthly') return { kind, day: Number(value.day) }
  throw new Error(`Fréquence inconnue : ${String(kind)}`)
}

export function ruleFromRow(row: RuleRow): RecurringRule {
  return {
    id: row.id,
    direction: row.direction,
    title: row.title,
    amount: row.amount,
    amountMode: row.amount_mode === 'variable' ? 'variable' : 'fixed',
    envelopeId: row.envelope_id,
    frequency: frequencyFromJson(row.frequency),
    startsOn: row.starts_on,
    endsOn: row.ends_on,
    reminderOffsets: row.reminder_offsets,
    reserveInAdvance: row.reserve_in_advance,
    isActive: row.is_active,
  }
}

export function ruleToRow(rule: RecurringRule): RuleInsert {
  return {
    id: rule.id,
    direction: rule.direction,
    title: rule.title.trim(),
    amount: rule.amount,
    amount_mode: rule.amountMode,
    envelope_id: rule.envelopeId,
    frequency: { ...rule.frequency },
    starts_on: rule.startsOn,
    ends_on: rule.endsOn,
    reminder_offsets: [...rule.reminderOffsets],
    reserve_in_advance: rule.reserveInAdvance,
    is_active: rule.isActive,
  }
}

export async function listRecurringRules(client: CentimeClient): Promise<RecurringRule[]> {
  const { data, error } = await client.from('recurring_rules').select('*').order('created_at')
  if (error) throw error
  return data.map(ruleFromRow)
}

export async function saveRecurringRule(
  client: CentimeClient,
  rule: RecurringRule,
): Promise<RecurringRule> {
  const { data, error } = await client
    .from('recurring_rules')
    .upsert(ruleToRow(rule))
    .select('*')
    .single()
  if (error) throw error
  return ruleFromRow(data)
}
